"use client";

import { type CSSProperties, type ReactNode, useRef } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "@zapier/i18n";
import styles from "./Table.module.css";
import BulkActions, { BulkSelectAction } from "./BulkActions/BulkActions";
import { useFreezeColumns } from "./useFreezeColumns";
import {
  BetaColumnHeader,
  BetaDataTableRoot as DataTable,
  BetaDataTableRouterProvider as RouterProvider,
  SortDescriptor,
} from "@zapier/design-system";
import RowLoader from "./RowLoader";

interface SelectableEntity {
  id?: string | number;
}

interface TableProps {
  allowSortByTitle?: boolean;
  ariaLabel: string;
  // Plural, user-facing asset label for the bulk-actions UI (e.g. "Zap workflows", "Tables").
  // Falls back to the translated "items" so bulk actions remain usable (and localized)
  // for tables that don't pass one.
  bulkActionsAssetLabel?: string;
  // Action items that should show in the bulk selection bar
  bulkSelectActions?: BulkSelectAction[];
  // Whether the bulk-actions dropdown should include the Zap-specific "Select paused" item
  showBulkSelectPaused?: boolean;
  // Message shown when bulk selection has no available actions (e.g. mixed types in trash)
  bulkActionsEmptyStateMessage?: string;
  // Rows
  children?: ReactNode;
  className?: string;
  columns: BetaColumnHeader[];
  isLoading?: boolean;
  onSortChange?: (sortDescriptor: SortDescriptor) => void;
  // Entities that will be selected when "Select all" is clicked
  selectableEntities?: SelectableEntity[];
  showCheckboxes?: boolean;
  showOwner?: boolean;
  sortDescriptor?: SortDescriptor;
  showOptions?: boolean;
  folderZapTableRow?: boolean;
  titleSortKey?: string;
  showShareButton?: boolean;
  minWidth?: number;
  /** Tooltip to show on sortable columns when sorting is disabled */
  sortDisabledTooltip?: string;
  showFavoriteButton?: boolean;
  /** Optional class for the Owner column header (e.g. to center align per table) */
  ownerColumnClassName?: string;
  /**
   * Override the Name column width (default 500px). Use a smaller value when the
   * row needs more room for trailing affordances (e.g. inline action buttons).
   */
  titleColumnWidth?: number;
}

const DEFAULT_TITLE_COLUMN_WIDTH = 500;

const Table = ({
  allowSortByTitle,
  ariaLabel,
  bulkActionsAssetLabel,
  bulkActionsEmptyStateMessage,
  bulkSelectActions,
  children,
  className,
  columns,
  isLoading,
  onSortChange,
  selectableEntities,
  showBulkSelectPaused,
  showCheckboxes,
  showOwner,
  sortDescriptor: _sortDescriptor,
  showOptions = true,
  folderZapTableRow,
  titleSortKey = "title",
  showShareButton = true,
  minWidth,
  sortDisabledTooltip,
  showFavoriteButton = true,
  ownerColumnClassName,
  titleColumnWidth = DEFAULT_TITLE_COLUMN_WIDTH,
}: TableProps) => {
  const tAssetTypes = useTranslations("asset-types");
  const sortDescriptor = _sortDescriptor ?? {
    direction: "ascending" as const,
    column: "",
  };
  const router = useRouter();
  // The Name + Options columns are frozen (horizontally pinned). In a narrow
  // container the two frozen ends would overlap and hide the middle columns, so
  // useFreezeColumns measures this container and returns whether the columns
  // should stay frozen. We feed that into the DS `isSticky` prop AND a remount
  // `key` on the DataTable below, so the DS cleanly drops/re-adds its own sticky
  // styling. Measured against the container, not the viewport, so a table in a
  // narrow pane/sidebar reflows too.
  const containerReference = useRef<HTMLDivElement>(null);
  const frozen = useFreezeColumns(containerReference);

  const onSort = onSortChange
    ? (descriptor: SortDescriptor) => {
        onSortChange(descriptor);
      }
    : undefined;
  const titleColumnWidthStyle = {
    "--title-column-width": `${titleColumnWidth}px`,
  } as CSSProperties;
  return (
    <RouterProvider navigate={router.push}>
      {/* Wrapper measured by useFreezeColumns to track the table's available
          width (fills the parent as a block; the DS scroll container inside
          handles overflow, so this stays at the container width). */}
      <div ref={containerReference}>
        <DataTable
          // Remount when the freeze state flips: the DS snapshots `isSticky` at
          // mount and won't clear its sticky styling when a column stops being
          // sticky, so a fresh mount is the only way to fully release it.
          key={frozen ? "frozen" : "unfrozen"}
          ariaLabel={ariaLabel}
          showCheckboxes={showCheckboxes}
          className={`${className || ""} hasFavorites`}
          style={titleColumnWidthStyle}
          onSortChange={onSort}
          sortDescriptor={sortDescriptor}
          isLoading={isLoading}
          minWidth={minWidth}
          rowLoader={
            <RowLoader
              columns={columns.length}
              showOptions={showOptions}
              showOwner={showOwner}
              showCheckboxes={showCheckboxes}
              rows={5}
              showShareButton={showShareButton}
            />
          }
          columns={
            [
              {
                allowsSorting: allowSortByTitle && !isLoading,
                isRowHeader: true,
                isSticky: frozen,
                label: "Name",
                sortKey: titleSortKey,
                className: `${styles.titleCell} ${folderZapTableRow ? styles.folderZapTableRowTitle : ""}`,
                sortDisabledTooltip,
                // DataTable / RAC column layout — min + default help when width:auto applies (desktop-large+)
                minWidth: titleColumnWidth,
                defaultWidth: titleColumnWidth,
              },
              ...columns,
              showOwner && {
                label: "Owner",
                className: ownerColumnClassName
                  ? `${styles.ownerCell} ${ownerColumnClassName}`
                  : styles.ownerCell,
              },
              showOptions && {
                isVisuallyHidden: true,
                isSticky: frozen,
                label: "Options",
                className: `${styles.iconButtonCell} ${showShareButton ? styles.hasShareButton : ""} ${showFavoriteButton ? styles.hasFavoriteButton : ""}`,
              },
            ].filter(Boolean) as BetaColumnHeader[]
          }
          bulkActionsComponent={
            <BulkActions
              assetTypeLabel={
                bulkActionsAssetLabel ?? tAssetTypes("plural.item")
              }
              selectableEntities={selectableEntities || []}
              bulkSelectActions={bulkSelectActions}
              showSelectPaused={showBulkSelectPaused}
              emptyStateMessage={bulkActionsEmptyStateMessage}
            />
          }
          sortDisabledTooltip={sortDisabledTooltip}
        >
          {children}
        </DataTable>
      </div>
    </RouterProvider>
  );
};

export default Table;
